import { useEffect, useMemo, useState } from 'react';
import { getLeaderboard, getMyLeaderboardRank } from '../api/api';
import { useAuth } from '../context/AuthContext';

const C = {
  red: '#E8192C',
  redGlow: 'rgba(232, 25, 44, 0.35)',
  orange: '#FF6B2B',
  green: '#3FCB7E',
  bg: '#0C0905',
  bgWarm: '#100B07',
  card: '#111111',
  cardAlt: '#16110D',
  border: '#1e1e1e',
  borderBright: '#2A2520',
  text: '#F0F2F5',
  textDim: '#9A938B',
  textVeryDim: '#5C554E',
};

const F = {
  display: '"Bebas Neue", "Oswald", Impact, sans-serif',
  body: '"Rajdhani", "Inter", system-ui, -apple-system, sans-serif',
};

const PAGE_SIZE = 10;

const scopeTabs = [
  { id: 'global', label: 'Global', active: true },
  { id: 'friends', label: 'Friends' },
];

const modeTabs = [
  { id: 'fivevfive', label: '5v5' },
  { id: 'wingman', label: 'Wingman' },
];

const windowTabs = [
  { id: 'season', label: 'Season', active: true },
  { id: 'weekly', label: 'Weekly' },
];

const winRate = (player) => Math.round((player.wins / Math.max(player.wins + player.losses, 1)) * 100);
const modeLabel = (mode) => modeTabs.find(tab => tab.id === mode)?.label || '5v5';

const normalizeLeaderboardRow = (row, currentUsername) => {
  const username = row?.users?.username || row?.username || 'Unknown Player';
  const elo = Number(row?.elo || row?.public_elo || 0);

  return {
    id: row?.id || row?.user_id || username,
    name: username,
    avatar: row?.users?.avatar_url || row?.avatar_url || null,
    tag: modeLabel(row?.mode),
    rank: row?.rank_name || 'Unranked',
    elo,
    wins: Number(row?.wins || 0),
    losses: Number(row?.losses || 0),
    matches: Number(row?.matches_played || 0),
    streak: Number(row?.win_streak || 0),
    score: elo,
    current: Boolean(currentUsername && username.toLowerCase() === currentUsername.toLowerCase()),
  };
};

const Diamond = ({ size = 10, color = C.red, filled = false, style }) => (
  <span
    aria-hidden="true"
    style={{
      width: size,
      height: size,
      display: 'inline-block',
      borderTop: `1.5px solid ${color}`,
      borderRight: `1.5px solid ${color}`,
      borderBottom: `1.5px solid ${color}`,
      borderLeft: `1.5px solid ${color}`,
      background: filled ? color : 'transparent',
      transform: 'rotate(45deg)',
      boxSizing: 'border-box',
      flexShrink: 0,
      ...style,
    }}
  />
);

const Eyebrow = ({ children }) => (
  <div style={s.eyebrow}>
    <Diamond size={7} color={C.red} filled />
    <span>{children}</span>
  </div>
);

const FilterButton = ({ active, children, disabled = true, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    aria-disabled={disabled ? 'true' : undefined}
    aria-pressed={active}
    title={disabled ? 'Coming soon' : undefined}
    onClick={onClick}
    style={{
      ...s.filterButton,
      ...(active ? s.filterButtonActive : null),
      ...(!disabled ? s.filterButtonEnabled : null),
    }}
  >
    {children}
  </button>
);

const RankAvatar = ({ player, large = false }) => (
  <div style={{ ...s.avatar, ...(large ? s.avatarLarge : null), borderLeftColor: player.current ? C.orange : C.red }}>
    {player.avatar
      ? <img src={player.avatar} alt="" style={s.avatarImg} />
      : <span>{player.name.slice(0, 2).toUpperCase()}</span>
    }
  </div>
);

const PodiumCard = ({ player, index, score }) => {
  const accent = index === 0 ? C.orange : C.red;

  return (
    <article className="rp-lb-podium-card" style={{ ...s.podiumCard, borderLeftColor: accent }}>
      <div style={s.podiumRank}>#{index + 1}</div>
      <RankAvatar player={player} large />
      <div>
        <div style={s.playerName}>{player.name}</div>
        <div style={s.playerMeta}>{player.rank} / {player.tag}</div>
      </div>
      <div style={s.podiumScore}>{score}</div>
      <div style={s.podiumLabel}>ELO</div>
      <div style={s.podiumFooter}>
        <span>{winRate(player)}% WR</span>
        <span>{player.streak > 0 ? `${player.streak}W streak` : 'Reset'}</span>
      </div>
    </article>
  );
};

const LeaderboardRow = ({ player, position, score }) => (
  <div id={player.current ? 'leaderboard-current-row' : undefined} className="rp-lb-row" style={{ ...s.row, ...(player.current ? s.currentRow : null) }}>
    <div style={s.position}>#{position}</div>
    <div style={s.playerCell}>
      <RankAvatar player={player} />
      <div>
        <div style={s.playerLine}>
          <span style={s.playerNameSmall}>{player.name}</span>
          {player.current && <span style={s.youBadge}>YOU</span>}
        </div>
        <div style={s.playerMeta}>{player.rank} / {player.tag}</div>
      </div>
    </div>
    <div style={s.metricStrong}>{score}</div>
    <div style={s.metric}>{player.wins}W / {player.losses}L</div>
    <div style={s.metric}>{winRate(player)}%</div>
    <div style={s.metric}>{player.streak > 0 ? `${player.streak}W` : '-'}</div>
    <div style={s.metric}>{player.matches}</div>
  </div>
);

export default function Leaderboard() {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ page: 1, limit: PAGE_SIZE, total: 0, total_pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [locating, setLocating] = useState(false);
  const [locateMessage, setLocateMessage] = useState('');
  const [locatedRank, setLocatedRank] = useState(null);
  const [selectedMode, setSelectedMode] = useState('fivevfive');

  useEffect(() => {
    let cancelled = false;

    const loadLeaderboard = async () => {
      setLoading(true);
      setError('');

      try {
        const res = await getLeaderboard({ page, limit: PAGE_SIZE, mode: selectedMode });
        const leaderboard = Array.isArray(res.data?.leaderboard) ? res.data.leaderboard : [];
        if (!cancelled) {
          setPlayers(leaderboard);
          setMeta({
            page: Number(res.data?.page || page),
            limit: Number(res.data?.limit || PAGE_SIZE),
            total: Number(res.data?.total || 0),
            total_pages: Number(res.data?.total_pages || 0),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Unable to load leaderboard.');
          setPlayers([]);
          setMeta({ page, limit: PAGE_SIZE, total: 0, total_pages: 0 });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadLeaderboard();

    return () => {
      cancelled = true;
    };
  }, [page, selectedMode]);

  const currentUsername = user?.username || '';
  const rows = useMemo(() => (
    players
      .map(row => normalizeLeaderboardRow(row, currentUsername))
  ), [players, currentUsername]);

  const leaders = rows.slice(0, 3);
  const currentPlayer = rows.find(player => player.current);
  const startRank = ((meta.page || page) - 1) * (meta.limit || PAGE_SIZE);
  const currentPosition = currentPlayer ? startRank + rows.findIndex(player => player.current) + 1 : locatedRank;
  const totalPages = meta.total_pages || 0;
  const pageNumbers = useMemo(() => {
    if (totalPages <= 1) return totalPages === 1 ? [1] : [];
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    const end = Math.min(totalPages, start + 4);
    return Array.from({ length: end - start + 1 }, (_, idx) => start + idx);
  }, [page, totalPages]);

  useEffect(() => {
    if (!currentPlayer || loading) return;
    const row = document.getElementById('leaderboard-current-row');
    row?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [currentPlayer, loading]);

  const goToPage = (nextPage) => {
    const safePage = Math.min(Math.max(1, nextPage), Math.max(totalPages, 1));
    setLocateMessage('');
    setPage(safePage);
  };

  const selectMode = (nextMode) => {
    setSelectedMode(nextMode);
    setPage(1);
    setLocatedRank(null);
    setLocateMessage('');
  };

  const locateYou = async () => {
    setLocating(true);
    setLocateMessage('');

    try {
      const res = await getMyLeaderboardRank(PAGE_SIZE, selectedMode);
      const data = res.data || {};

      if (!data.is_ranked) {
        setLocatedRank(null);
        setLocateMessage(`Finish ${data.placement_games_required || (selectedMode === 'wingman' ? 3 : 5)} ranked ${modeLabel(selectedMode)} placements to enter the ladder.`);
        return;
      }

      setLocatedRank(Number(data.rank || 0));
      setLocateMessage(`Located you at #${data.rank}.`);
      setPage(Number(data.page || 1));
    } catch (err) {
      setLocatedRank(null);
      setLocateMessage(err?.response?.data?.detail || 'Unable to locate your leaderboard rank.');
    } finally {
      setLocating(false);
    }
  };

  return (
    <main style={s.root}>
      <style>{`
        @keyframes rp-lb-page-up{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
        .rp-lb-podium{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:16px}
        .rp-lb-grid{display:grid;grid-template-columns:minmax(0,1fr) 320px;gap:18px;align-items:start}
        .rp-lb-table-head,.rp-lb-row{display:grid;grid-template-columns:64px minmax(220px,1fr) 110px 120px 90px 90px 80px;align-items:center;gap:12px}
        .rp-lb-podium-card,.rp-lb-side-card,.rp-lb-board{animation:rp-lb-page-up 420ms ease both}
        .rp-lb-row{transition:background 160ms ease,border-color 160ms ease,transform 160ms ease}
        .rp-lb-row:hover{background:rgba(255,255,255,0.035);border-left-color:${C.orange};transform:translateX(2px)}
        .rp-lb-scroll{overflow-x:auto}
        @media (max-width:1180px){
          .rp-lb-grid{grid-template-columns:1fr}
          .rp-lb-podium{grid-template-columns:1fr}
        }
        @media (max-width:860px){
          .rp-lb-table-head{display:none}
          .rp-lb-row{grid-template-columns:48px minmax(150px,1fr) 76px 54px;gap:10px}
          .rp-lb-row > div:nth-child(4),.rp-lb-row > div:nth-child(5),.rp-lb-row > div:nth-child(6){display:none}
          .rp-lb-row > div:nth-child(7){justify-self:end}
        }
        @media (max-width:640px){
          .rp-lb-filter-row{grid-template-columns:1fr}
          .rp-lb-filter-group{width:100%;overflow-x:auto}
        }
        @media (prefers-reduced-motion:reduce){
          *{animation-duration:1ms!important;transition-duration:1ms!important}
        }
      `}</style>

      <section style={s.hero}>
        <div>
          <Eyebrow>Season command board</Eyebrow>
          <h1 style={s.title}>Leaderboards</h1>
          <p style={s.subtitle}>
            Track the top Repeaked players by public ranked ELO, record, and current form per mode.
          </p>
        </div>
        <div style={s.seasonCard}>
          <div style={s.seasonLabel}>Active Season</div>
          <div style={s.seasonValue}>Season BETA</div>
          <div style={s.seasonMeta}>{modeLabel(selectedMode)} ladder / placements required</div>
        </div>
      </section>

      <section className="rp-lb-filter-row" style={s.filters}>
        <div className="rp-lb-filter-group" style={s.filterGroup} aria-label="Leaderboard scope">
          {scopeTabs.map(tab => (
            <FilterButton key={tab.id} active={tab.active}>
              {tab.label}
            </FilterButton>
          ))}
        </div>
        <div className="rp-lb-filter-group" style={s.filterGroup} aria-label="Leaderboard mode">
          {modeTabs.map(tab => (
            <FilterButton
              key={tab.id}
              active={selectedMode === tab.id}
              disabled={false}
              onClick={() => selectMode(tab.id)}
            >
              {tab.label}
            </FilterButton>
          ))}
        </div>
        <div className="rp-lb-filter-group" style={s.filterGroup} aria-label="Leaderboard window">
          {windowTabs.map(tab => (
            <FilterButton key={tab.id} active={tab.active}>
              {tab.label}
            </FilterButton>
          ))}
        </div>
      </section>

      {!loading && !error && leaders.length > 0 && (
        <section className="rp-lb-podium" style={s.section}>
          {leaders.map((player, index) => (
            <PodiumCard key={player.id} player={player} index={index} score={player.score} />
          ))}
        </section>
      )}

      <section className="rp-lb-grid" style={s.section}>
        <div className="rp-lb-board" style={s.board}>
          <div style={s.cardHeader}>
            <div>
              <Eyebrow>Ranked standings</Eyebrow>
              <h2 style={s.cardTitle}>{modeLabel(selectedMode)} Ladder</h2>
            </div>
            <div style={s.boardActions}>
              <button type="button" onClick={locateYou} disabled={locating || loading} style={s.locateButton}>
                {locating ? 'Locating...' : 'Locate You'}
              </button>
              <div style={s.resultCount}>
                {loading ? 'Syncing' : `${meta.total} players`}
              </div>
            </div>
          </div>

          {locateMessage && (
            <div role="status" style={s.locateMessage}>{locateMessage}</div>
          )}

          {loading && (
            <div style={s.statePanel}>SYNCING GLOBAL STANDINGS...</div>
          )}

          {!loading && error && (
            <div role="alert" style={{ ...s.statePanel, ...s.errorPanel }}>{error}</div>
          )}

          {!loading && !error && rows.length === 0 && (
            <div style={s.statePanel}>No ranked players yet.</div>
          )}

          {!loading && !error && rows.length > 0 && (
            <div className="rp-lb-scroll">
            <div className="rp-lb-table-head" style={s.tableHead}>
              <span>Rank</span>
              <span>Player</span>
              <span>Rating</span>
              <span>Record</span>
              <span>Win Rate</span>
              <span>Streak</span>
              <span>Matches</span>
            </div>
            <div style={s.rowStack}>
              {rows.map((player, index) => (
                <LeaderboardRow key={player.id} player={player} position={startRank + index + 1} score={player.score} />
              ))}
            </div>
            </div>
          )}

          {!loading && !error && totalPages > 1 && (
            <div style={s.pagination}>
              <button
                type="button"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                style={{ ...s.pageButton, ...(page <= 1 ? s.pageButtonDisabled : null) }}
              >
                Prev
              </button>
              {pageNumbers.map(pageNumber => (
                <button
                  key={pageNumber}
                  type="button"
                  onClick={() => goToPage(pageNumber)}
                  disabled={pageNumber === page}
                  style={{
                    ...s.pageButton,
                    ...(pageNumber === page ? s.pageButtonActive : null),
                  }}
                >
                  {pageNumber}
                </button>
              ))}
              <button
                type="button"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                style={{ ...s.pageButton, ...(page >= totalPages ? s.pageButtonDisabled : null) }}
              >
                Next
              </button>
            </div>
          )}
        </div>

        <aside style={s.sideStack}>
          <div className="rp-lb-side-card" style={s.sideCard}>
            <Eyebrow>Your placement</Eyebrow>
            {currentPlayer ? (
              <>
                <div style={s.currentPlacement}>
                  <RankAvatar player={currentPlayer} large />
                  <div>
                    <div style={s.sideTitle}>#{currentPosition}</div>
                    <div style={s.playerMeta}>{currentPlayer.rank}</div>
                  </div>
                </div>
                <div style={s.sideMetricGrid}>
                  <div>
                    <div style={s.sideMetricLabel}>ELO</div>
                    <div style={s.sideMetricValue}>{currentPlayer.elo}</div>
                  </div>
                  <div>
                    <div style={s.sideMetricLabel}>Win Rate</div>
                    <div style={s.sideMetricValue}>{winRate(currentPlayer)}%</div>
                  </div>
                </div>
              </>
            ) : (
              <div style={s.sideEmpty}>
                Finish your {modeLabel(selectedMode)} placements to appear in this ladder.
              </div>
            )}
          </div>

          <div className="rp-lb-side-card" style={s.sideCard}>
            <Eyebrow>Ranking rules</Eyebrow>
            <div style={s.ruleList}>
              <div style={s.ruleItem}><Diamond size={7} filled /> Standings sort by public ELO after placements.</div>
              <div style={s.ruleItem}><Diamond size={7} filled /> Ties favor higher win rate, then active streak.</div>
              <div style={s.ruleItem}><Diamond size={7} filled /> 5v5 reveals after 5 placements; Wingman reveals after 3.</div>
            </div>
          </div>

          <div className="rp-lb-side-card" style={s.calloutCard}>
            <div style={s.calloutLabel}>Tracked bracket</div>
            <div style={s.calloutValue}>Top 50</div>
            <div style={s.calloutText}>Only ranked players with completed placements appear on the selected mode ladder.</div>
          </div>
        </aside>
      </section>
    </main>
  );
}

const s = {
  root: {
    minHeight: '100%',
    padding: '30px clamp(16px, 3vw, 34px) 44px',
    background:
      `radial-gradient(circle at 18% 0%, rgba(232,25,44,0.12), transparent 28%),
       radial-gradient(circle at 100% 18%, rgba(255,107,43,0.08), transparent 30%),
       ${C.bg}`,
    color: C.text,
    fontFamily: F.body,
  },
  hero: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 24,
    alignItems: 'flex-start',
    maxWidth: 1320,
    margin: '0 auto',
  },
  eyebrow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    color: C.textDim,
    fontFamily: F.body,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 2.4,
    textTransform: 'uppercase',
  },
  title: {
    margin: '12px 0 6px',
    fontFamily: F.display,
    fontSize: 'clamp(44px, 7vw, 86px)',
    lineHeight: 0.92,
    letterSpacing: 1.6,
    textTransform: 'uppercase',
  },
  subtitle: {
    maxWidth: 680,
    margin: 0,
    color: C.textDim,
    fontSize: 16,
    lineHeight: 1.55,
    fontWeight: 600,
  },
  seasonCard: {
    minWidth: 260,
    background: C.card,
    borderTop: `1px solid ${C.borderBright}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${C.orange}`,
    padding: 20,
    boxShadow: '0 18px 42px rgba(0,0,0,0.28)',
  },
  seasonLabel: {
    color: C.textDim,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 2,
    textTransform: 'uppercase',
  },
  seasonValue: {
    marginTop: 8,
    fontFamily: F.display,
    fontSize: 30,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  seasonMeta: {
    color: C.textDim,
    fontSize: 13,
    fontWeight: 700,
  },
  filters: {
    maxWidth: 1320,
    margin: '24px auto 0',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    gap: 12,
    alignItems: 'center',
  },
  filterGroup: {
    display: 'flex',
    gap: 8,
    minWidth: 0,
  },
  filterButton: {
    minHeight: 42,
    padding: '0 15px',
    background: 'rgba(255,255,255,0.015)',
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `1px solid ${C.border}`,
    color: C.textVeryDim,
    fontFamily: F.body,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 1.6,
    textTransform: 'uppercase',
    cursor: 'not-allowed',
    opacity: 0.62,
    filter: 'none',
    transform: 'none',
    whiteSpace: 'nowrap',
  },
  filterButtonActive: {
    background: 'rgba(154,147,139,0.08)',
    borderTopColor: C.borderBright,
    borderRightColor: C.borderBright,
    borderBottomColor: C.borderBright,
    borderLeftColor: C.borderBright,
    color: C.textDim,
  },
  filterButtonEnabled: {
    cursor: 'pointer',
    opacity: 1,
    color: C.text,
  },
  section: {
    maxWidth: 1320,
    margin: '18px auto 0',
  },
  podiumCard: {
    position: 'relative',
    overflow: 'hidden',
    minHeight: 260,
    background: `linear-gradient(145deg, rgba(22,17,13,0.98), rgba(17,17,17,0.98))`,
    borderTop: `1px solid ${C.borderBright}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${C.red}`,
    padding: 22,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    boxShadow: '0 22px 44px rgba(0,0,0,0.28)',
  },
  podiumRank: {
    position: 'absolute',
    top: 16,
    right: 18,
    color: 'rgba(240,242,245,0.07)',
    fontFamily: F.display,
    fontSize: 82,
    lineHeight: 0.8,
    letterSpacing: 1,
  },
  avatar: {
    width: 42,
    height: 42,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: C.cardAlt,
    borderTop: `1px solid ${C.borderBright}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${C.red}`,
    color: C.text,
    fontFamily: F.display,
    fontSize: 17,
    letterSpacing: 1,
    flexShrink: 0,
    overflow: 'hidden',
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  avatarLarge: {
    width: 62,
    height: 62,
    fontSize: 24,
  },
  playerName: {
    fontFamily: F.display,
    fontSize: 30,
    letterSpacing: 1.1,
    textTransform: 'uppercase',
  },
  playerNameSmall: {
    fontFamily: F.body,
    fontSize: 16,
    fontWeight: 800,
    color: C.text,
  },
  playerLine: {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
  },
  playerMeta: {
    color: C.textDim,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1.1,
    textTransform: 'uppercase',
  },
  podiumScore: {
    marginTop: 'auto',
    color: C.orange,
    fontFamily: F.display,
    fontSize: 54,
    lineHeight: 0.9,
    letterSpacing: 1.3,
    textShadow: `0 0 18px ${C.redGlow}`,
  },
  podiumLabel: {
    color: C.textDim,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 2,
  },
  podiumFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    color: C.text,
    borderTop: `1px solid ${C.border}`,
    paddingTop: 13,
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: 1.1,
    textTransform: 'uppercase',
  },
  board: {
    background: C.card,
    borderTop: `1px solid ${C.borderBright}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${C.red}`,
    boxShadow: '0 22px 44px rgba(0,0,0,0.26)',
    minWidth: 0,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    padding: 22,
    borderBottom: `1px solid ${C.border}`,
  },
  cardTitle: {
    margin: '8px 0 0',
    fontFamily: F.display,
    fontSize: 34,
    lineHeight: 1,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  boardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  locateButton: {
    minHeight: 38,
    padding: '0 14px',
    background: 'rgba(232,25,44,0.12)',
    borderTop: `1px solid ${C.red}`,
    borderRight: `1px solid ${C.red}`,
    borderBottom: `1px solid ${C.red}`,
    borderLeft: `1px solid ${C.red}`,
    color: C.text,
    fontFamily: F.body,
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  locateMessage: {
    padding: '12px 22px',
    color: C.textDim,
    background: C.bgWarm,
    borderTop: 'none',
    borderRight: 'none',
    borderBottom: `1px solid ${C.border}`,
    borderLeft: 'none',
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: 1.3,
    textTransform: 'uppercase',
  },
  resultCount: {
    color: C.textDim,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 1.6,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  statePanel: {
    minHeight: 220,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 28,
    color: C.textDim,
    background: C.bgWarm,
    borderTop: 'none',
    borderRight: 'none',
    borderBottom: `1px solid ${C.border}`,
    borderLeft: 'none',
    fontFamily: F.body,
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: 2,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  errorPanel: {
    color: C.red,
    background: 'rgba(232,25,44,0.08)',
  },
  tableHead: {
    minWidth: 840,
    padding: '14px 22px',
    color: C.textVeryDim,
    background: C.bgWarm,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
  },
  rowStack: {
    minWidth: 840,
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 18,
    background: C.bgWarm,
    borderTop: `1px solid ${C.border}`,
  },
  pageButton: {
    minWidth: 42,
    minHeight: 38,
    padding: '0 12px',
    background: 'transparent',
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `1px solid ${C.border}`,
    color: C.textDim,
    fontFamily: F.body,
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  pageButtonActive: {
    background: 'rgba(232,25,44,0.12)',
    borderTopColor: C.red,
    borderRightColor: C.red,
    borderBottomColor: C.red,
    borderLeftColor: C.red,
    color: C.text,
    cursor: 'default',
  },
  pageButtonDisabled: {
    color: C.textVeryDim,
    cursor: 'not-allowed',
    opacity: 0.5,
  },
  row: {
    padding: '15px 22px',
    borderTop: 'none',
    borderRight: 'none',
    borderBottom: `1px solid ${C.border}`,
    borderLeft: '2px solid transparent',
    background: 'transparent',
  },
  currentRow: {
    background: 'rgba(255,107,43,0.07)',
    borderLeftColor: C.orange,
  },
  position: {
    color: C.textDim,
    fontFamily: F.display,
    fontSize: 24,
    letterSpacing: 1,
  },
  playerCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  metricStrong: {
    color: C.text,
    fontFamily: F.display,
    fontSize: 26,
    letterSpacing: 1,
  },
  metric: {
    color: C.textDim,
    fontSize: 14,
    fontWeight: 800,
  },
  youBadge: {
    padding: '2px 7px',
    background: 'rgba(255,107,43,0.14)',
    color: C.orange,
    borderTop: `1px solid rgba(255,107,43,0.35)`,
    borderRight: `1px solid rgba(255,107,43,0.35)`,
    borderBottom: `1px solid rgba(255,107,43,0.35)`,
    borderLeft: `1px solid rgba(255,107,43,0.35)`,
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 1.4,
  },
  sideStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  sideCard: {
    background: C.card,
    borderTop: `1px solid ${C.borderBright}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${C.red}`,
    padding: 20,
    boxShadow: '0 18px 36px rgba(0,0,0,0.24)',
  },
  currentPlacement: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    marginTop: 18,
  },
  sideTitle: {
    fontFamily: F.display,
    fontSize: 46,
    lineHeight: 0.9,
    letterSpacing: 1.2,
  },
  sideMetricGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    marginTop: 18,
  },
  sideEmpty: {
    marginTop: 18,
    color: C.textDim,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.45,
  },
  sideMetricLabel: {
    color: C.textDim,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
  },
  sideMetricValue: {
    marginTop: 4,
    color: C.text,
    fontFamily: F.display,
    fontSize: 30,
    letterSpacing: 1,
  },
  ruleList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 16,
  },
  ruleItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    color: C.textDim,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.45,
  },
  calloutCard: {
    background: `linear-gradient(145deg, rgba(232,25,44,0.15), rgba(17,17,17,0.98))`,
    borderTop: `1px solid rgba(255,107,43,0.35)`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${C.orange}`,
    padding: 20,
  },
  calloutLabel: {
    color: C.textDim,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
  },
  calloutValue: {
    marginTop: 8,
    fontFamily: F.display,
    fontSize: 48,
    lineHeight: 0.95,
    letterSpacing: 1.2,
    color: C.orange,
  },
  calloutText: {
    marginTop: 10,
    color: C.textDim,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.45,
  },
};
